import PropTypes from 'prop-types';
import TableBasic from '../../../atoms/table-basic/index';
import styles from './fund-detail-table.module.scss';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import format from '../../../../utils/format';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { getTextValueColorClass, isVariableAnnuity, isTiaaFixedAnnuity } from '../../../../utils/sharedHelpers';
import { FUND_TYPES } from '../../../../stores/profile/get-fund-type';
import parse from 'html-react-parser';
import { ASSET_CLASS } from '../../../../shared/screener-constants';
import PerformanceValue from '../../../../components/atoms/performance-value';
import { isCIT } from '../../../../utils/fund-type-helpers/fundTypeHelpers';

const className = augmentClassName(styles);

const FundDetailTable = ({ fundDetails, fundType, assetClass, isProp }) => {
  const isCITFund = isCIT(isProp, fundType);
  return (
    <div {...className('table-container')}>
      <TableBasic className={'no-top-border'} flush screener borderless>
        <caption {...className('sr-only')}>Fund details</caption>
        {isCITFund && renderCITRows(fundDetails, assetClass)}
        {isVariableAnnuity(fundType) && !isCITFund && renderVARows(fundDetails, assetClass)}
        {isTiaaFixedAnnuity(fundType) && renderFixedAnnuityRows(fundDetails, fundType)}
        {FUND_TYPES.mutualFund === fundType && renderMutualFundRows(fundDetails, assetClass, isProp)}
        {FUND_TYPES.fdicInsuredBankProduct === fundType && renderFdicRows(fundDetails)}
        {(FUND_TYPES.retirePlus === fundType || FUND_TYPES.customPortfolioModel === fundType) && renderRetirePlusRows(fundDetails)}
      </TableBasic>
    </div>
  );
};

FundDetailTable.propTypes = {
  assetClass: PropTypes.string,
  fundDetails: PropTypes.object,
  fundType: PropTypes.string,
  isProp: PropTypes.bool
};

const RatingBar = ({ rating }) => {
  return (
    <div {...className('width')}>
      <Row {...className('bar', 'w-100')}>
        <span {...className('sr-only')}>
          {rating >= 1 && rating <= 5
            ? `${format.number(rating, {
              formatString: '0'
            })} out of 5 risk level vs peers`
            : 'Rating Unavailable'}
        </span>
        {[1, 2, 3, 4, 5].map(ordinal => (
          <span key={ordinal} {...className('rating-band', ordinal > rating && 'empty')} />
        ))}
      </Row>
      <Row>
        <Col {...className('label', 'left')}>Low</Col>
        <Col {...className('label')}>High</Col>
      </Row>
    </div>
  );
};

RatingBar.propTypes = {
  rating: PropTypes.number
};

function renderVARows(fundDetails, assetClass) {
  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPercentRow({
        label: 'Daily YTD',
        value: fundDetails.dailyYtd,
        date: fundDetails.dailyYtdAsOf,
        renderSuperScriptOnHeader: false
      })}
      {renderPriceRow({
        label: 'NAV / Unit Value',
        value: fundDetails.nav,
        superscript: fundDetails.navSuperScript
      })}
      {renderPriceRow({
        label: "Day's Change",
        value: fundDetails.dayChange,
        percentValue: fundDetails.dayChangePct
      })}
      {assetClass === ASSET_CLASS.MONEYMARKET &&
        renderYieldRow({
          label: '7-Day Current /<br> Effective Yield',
          value: fundDetails.annualYield7Day,
          secondValue: fundDetails.effectiveYield7Day,
          date: fundDetails.yieldAsOfDate,
          superscript: fundDetails.yield7DaySuperScript
        })}
    </tbody>
  );
}

function renderMutualFundRows(fundDetails, assetClass, isProp) {
  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPercentRow({
        label: 'Daily YTD',
        value: fundDetails.dailyYtd,
        date: fundDetails.dailyYtdAsOf
      })}
      {renderPriceRow({ label: 'NAV / Unit Value', value: fundDetails.nav, superscript: fundDetails.navSuperScript })}
      {renderPriceRow({
        label: "Day's Change",
        value: fundDetails.dayChange,
        percentValue: fundDetails.dayChangePct
      })}
      {yieldRowHelper({ fundDetails, assetClass, isProp })}
    </tbody>
  );
}

function renderFdicRows(fundDetails) {
  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPercentRow({
        label: 'Current APY',
        value: fundDetails.currentApy,
        superscript: fundDetails.currentApySuperScript
      })}
      {renderCurrentInterstRatePercentRow({
        label: 'Current Interest Rate',
        value: fundDetails.currentInterestRate,
        superscript: fundDetails.currentInterestRateSuperScript
      })}
    </tbody>
  );
}

function renderFixedAnnuityRows(fundDetails, fundType) {
  const currentRate =
    fundType === FUND_TYPES.fixedAnnuityTiaaStableValue
      ? fundDetails.tsvCurrentRate
      : fundDetails.currentRate;

  const minimumGuaranteedRate =
    fundType === FUND_TYPES.fixedAnnuityTiaaStableValue
      ? fundDetails.tsvMinGuaranteedRate
      : fundDetails.minGuaranteedRate;

  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPercentRow({
        label: 'Current Rates',
        value: currentRate,
        superscript: fundDetails.currentRateSuperScript
      })}
      {renderPercentRow({ label: 'Minimum Guaranteed Rate', value: minimumGuaranteedRate })}
    </tbody>
  );
}

function renderRetirePlusRows(fundDetails) {
  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPercentRow({ label: '3 Month Total Return', value: fundDetails.threeMonthReturn })}
    </tbody>
  );
}

function renderCITRows(fundDetails, assetClass) {
  return (
    <tbody>
      {renderRiskRow(fundDetails.risk)}
      {renderPriceRow({ label: 'NAV / Unit Value', value: fundDetails.nav, superscript: fundDetails.navSuperScript })}
      {renderPriceRow({
        label: "Day's Change",
        value: fundDetails.dayChange,
        percentValue: fundDetails.dayChangePct
      })}
      {assetClass === ASSET_CLASS.MONEYMARKET && yieldRowHelper({ fundDetails, assetClass })}
    </tbody>
  );
}

// eslint-disable-next-line react/prop-types
function renderPercentRow({ label, value, date, superscript, renderSuperScriptOnHeader = true }) {
  return (
    <tr {...className('border-bottom')}>
      <th scope="row" {...className('text-left', 'text-nowrap')}>
        {label}
        {superscript && renderSuperScriptOnHeader && <sup>{superscript}</sup>}
      </th>
      <td {...className('text-right', getTextValueColorClass(value))}>
        <PerformanceValue value={value} isStyleTextColor={false} sizeKey={'min'} />
        {superscript && !renderSuperScriptOnHeader && <sup {...className('text-black')}>{superscript}</sup>}
        {date && <span {...className('text-black', 'd-block', 'text-muted', 'font-size-10')}>As of {format.oaDate(date)}</span>}
      </td>
    </tr>
  );
}

// eslint-disable-next-line react/prop-types
function renderCurrentInterstRatePercentRow({ label, value, date, superscript }) {
  return (
    <tr {...className('border-bottom')}>
      <th scope="row" {...className('text-left', 'text-nowrap')}>
        {label}
        {superscript && <sup>{superscript}</sup>}
      </th>
      <td {...className('text-right', getTextValueColorClass(value))}>
        {format.percent(value, { formatString: '0.000' })}
        {date && <span {...className('text-black', 'd-block', 'text-muted', 'font-size-10')}>As of {format.oaDate(date)}</span>}
      </td>
    </tr>
  );
}

// eslint-disable-next-line react/prop-types
function renderPriceRow({ label, value, percentValue, superscript }) {
  return (
    <tr {...className('border-bottom')}>
      <th scope="row" {...className('text-left', 'text-nowrap')}>
        {label}
        {superscript && <sup>{superscript}</sup>}
      </th>
      <td {...className('text-right', getTextValueColorClass(value))}>
        {format.price(value)}
        {percentValue && (
          <span {...className('text-black')}>&nbsp; / <PerformanceValue value={percentValue} isStyleTextColor={false} sizeKey={'min'} /></span>
        )}
      </td>
    </tr>
  );
}

function renderRiskRow(riskValue) {
  return (
    <tr {...className('border-bottom')}>
      <th scope="row" {...className('text-left')}>
        Risk
      </th>
      <td {...className('text-right')}>{riskValue ? <RatingBar rating={riskValue} /> : 'N/A'}</td>
    </tr>
  );
}

// eslint-disable-next line react/prop-types
function yieldRowHelper({ fundDetails, assetClass, isProp }) {
  if (assetClass === ASSET_CLASS.MONEYMARKET) {
    return renderYieldRow({
      label: '7-Day Current /<br> Effective Yield',
      value: fundDetails.annualYield7Day,
      secondValue: fundDetails.effectiveYield7Day,
      date: fundDetails.yieldAsOfDate,
      superscript: fundDetails.yield7DaySuperScript
    });
  } else if (assetClass === ASSET_CLASS.FIXEDINCOME && isProp) {
    return renderYieldRow({
      label: '30-Day SEC Yield <br> (Sub / UnSub)',
      value: fundDetails.annualYield30Day,
      secondValue: fundDetails.annualYield30dayUnsub,
      date: fundDetails.yieldAsOfDate,
      superscript: fundDetails.annualYield30DaySuperScript
    });
  }
  return renderYieldRow({
    label: '30-Day SEC Yield',
    value: fundDetails.annualYield30Day,
    date: fundDetails.yieldAsOfDate,
    superscript: fundDetails.annualYield30DaySuperScript
  });

}

// eslint-disable-next-line react/prop-types
function renderYieldRow({ label, value, secondValue, date, superscript }) {
  return (
    <tr {...className('border-bottom')}>
      <th scope="row" {...className('text-left', 'text-nowrap')}>
        {parse(label)}
        {superscript && <sup>{superscript}</sup>}
      </th>
      {
        secondValue &&
        <td {...className('text-right', getTextValueColorClass(value))}>
          <PerformanceValue value={value} isStyleTextColor={false} sizeKey={'min'} />
          &nbsp;/&nbsp;
          <PerformanceValue value={secondValue} sizeKey={'min'} />
          {date && <span {...className('text-black', 'd-block', 'text-muted', 'font-size-10')}>As of {format.oaDate(date)}</span>}
        </td>
      }
      {
        !secondValue &&
        <td {...className('text-right', getTextValueColorClass(value))}>
          <PerformanceValue value={value} isStyleTextColor={false} sizeKey={'min'} />
          {date && <span {...className('text-black', 'd-block', 'text-muted', 'font-size-10')}>As of {format.oaDate(date)}</span>}
        </td>
      }
    </tr>
  );
}

export default FundDetailTable;
